import { Alignment, Api, CurrencySettings, FieldUpdateEvent, ModelRow, StringUtil } from "@mcleod/core";
import { DataSourceAction, Layout, Image, Textbox, Table, DataSourceMode } from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { AutogenLayoutBillingHistoryOverview } from "./autogen/AutogenLayoutBillingHistoryOverview";
import { BillingCommentsQuickInfo } from "./BillingCommentsQuickInfo";
import { IntBillCommentsQuickInfo } from "./IntBillCommentsQuickInfo";
import { ExtBillCommentsQuickInfo } from "./ExtBillCommentsQuickInfo";
import { createCommentModelRows } from "./InvoiceProcessingUtil";

export class BillingHistoryOverview extends AutogenLayoutBillingHistoryOverview {

    override onLoad() {
        this.textboxContactName._captionLabel.color = "primary.reverse";
        this.textboxSalespersonId._captionLabel.color = "primary.reverse";
        this.textboxName._captionLabel.color = "primary.reverse";
        this.panelDataFusion.visible = false;
    }

    async sourceOrdersAfterExecution(event) {
        const row = this.sourceOrders.activeRow;
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setCarrierRateData();
            const customerId = row?.get("customer_id");
            if (customerId != null) {
                await this.sourceCustomer.search({ "id": customerId });
            }
        }
    }

    async sourceBillingAfterExceution(event) {
        const row = this.mainDataSource.activeRow;
        if (row?.getFirstLookupModelData("commodity")) {
            this.textboxCommodity.text = row?.getFirstLookupModelData("commodity")?.get("descr");
        }
        this.addReferenceNumberTextboxes();
        if (event.getAction() === DataSourceAction.SEARCH && row) {
            row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => this.billingFieldUpdated(event))
        }
        if (this.mainDataSource?.activeRow?.get("order_id")) {
            await this.sourceOrders.search({ id: this.mainDataSource?.activeRow?.get("order_id") });
        }
    }

    billingFieldUpdated(event: FieldUpdateEvent) {
        if ("customer_id" == event.fieldName && StringUtil.isEmptyString(event.newValue))
            this.customerChanged(null);
        else if ("commodity" == event.fieldName)
            this.textboxCommodity.text = event.originator?.text || "--";
    }

    textboxNameOnDataDisplay() {
        CommonTooltips.setTooltipContentLayout(this.textboxName, this.activeRow?.get("customer_id"), "lme/ar/CustomerQuickInfo");
    }

    customerChanged(customerData: ModelRow<any>) {
        this.sourceCustomer.data = [];
        if (customerData != null) {
            this.sourceCustomer.data = [customerData];
            this.sourceCustomer.setRowIndexWithoutUpdate(0);
        }
        this.sourceCustomer.displayDataInBoundComponents();
        this.setBridgeCustomerLabel();
    }

    private addReferenceNumberTextboxes() {
        this.panelRefNbrs.removeAll();
        const row = this.mainDataSource.activeRow;
        this.addReferenceNumberTextbox("BOL", row.get("blnum"), "blnum");
        this.addReferenceNumberTextbox("Consignee Reference Number", row.get("consignee_ref_no"), "consignee_refno");
    }

    addReferenceNumberTextbox = function (caption: string, text: string, field?: string) {
        if (caption != null && text != null) {
            const textbox = new Textbox({ marginBottom: 3, caption: caption, text: text, printable: true, fontSize: "large", fontBold: true, fillRow: true });
            textbox._captionLabel.fontSize = "medium";
            if (field != null) {
                textbox.field = field;
                textbox.dataSource = this.mainDataSource;
            }
            this.panelRefNbrs.add(textbox);
        }
    }

    setCarrierRateData() {
        const moveId = this.sourceOrders.activeRow.get("movement.id");
        const orderFreightCharge = this.sourceOrders.activeRow?.getCurrency("freight_charge");
        const orderCurrencyCode = orderFreightCharge?.getCurrencyCode();
        const orderConversionDate = orderFreightCharge?.getConversionDate();
        let moveCurrencyCode: string = null;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const moveCarrierPay = this.sourceOrders.activeRow?.getCurrency("movement.override_pay_amt");
            moveCurrencyCode = moveCarrierPay != null ? moveCarrierPay.getCurrencyCode() : orderCurrencyCode;
        }
        if (moveId != null) {
            this.sourceCalculateOverridePay.search({
                "movement_id": moveId,
                "order_id": this.sourceOrders.activeRow?.get("id"),
                "calculate_order_total": true,
                "move_currency_code": moveCurrencyCode,
                "conversion_date": orderConversionDate
            });
        }
    }

    async sourceOrderOnDisplay() {
        const movementId = this.sourceOrders.activeRow?.get("movement.id") as string;
        const orderId = this.sourceOrders.activeRow?.get("id") as string;
        const customerId = this.mainDataSource.activeRow?.get("customer_id") as string;
        const companyId = this.sourceOrders.activeRow?.get("company_id") as string;
        const pbLtl = this.sourceOrders.activeRow?.get("is_brokerage_ltl");
        const hasIntercompany = this.sourceOrders.activeRow?.get("has_intercompany") as boolean;
        const hasICerror = this.sourceOrders.activeRow?.get("has_ic_error") as boolean;

        const layoutOrderDataFusionSummary = this.layoutOrderDataFusionSummary;
        if (movementId && orderId && customerId) {
            layoutOrderDataFusionSummary.movementId = movementId;
            layoutOrderDataFusionSummary.orderId = orderId;
            layoutOrderDataFusionSummary.customerId = customerId;
            layoutOrderDataFusionSummary.companyId = companyId;
            layoutOrderDataFusionSummary.hasIntercompany = hasIntercompany;
            layoutOrderDataFusionSummary.hasICError = hasICerror;

            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId,
                    is_brokerage_ltl: pbLtl
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/order-datafusion-summary", body).then(response => {
                if (response) {
                    layoutOrderDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutOrderDataFusionSummary.displayingData;
                }
            });
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async showErrorPanel(ySplit: boolean) {
        let showPanel = false;
        const activeRow = this.mainDataSource.activeRow;

        if (activeRow) {
            showPanel = this.updateVisibilityBasedOnFlags(activeRow) || showPanel;
        }

        if (await this.setCommentsInfo()) {
            this.imageComments.visible = true;
            this.setCommentsQuickInfo(this.imageComments);
            showPanel = true;
        }

        if (ySplit) {
            this.imageYSplit.visible = true;
            showPanel = true;
        }

        if (this.sourceCustomer?.activeRow?.get("summary_billing") === "Y") {
            this.imageSummaryInvoice.visible = true;
            showPanel = true;
        }

        if (showPanel) {
            this.panelIcons.visible = true;
        }
    }

    private updateVisibilityBasedOnFlags(activeRow: ModelRow<any>): boolean {
        let showPanel = false;

        const flagMappings = [
            { field: "is_edi_customer", image: this.imageDatafusionCustomer },
            { field: "is_summary_bill", image: this.imageSummaryInvoice }
        ];

        flagMappings.forEach(({ field, image }) => {
            if (activeRow.get(field) === "Y" || activeRow.get(field) === true) {
                image.visible = true;
                showPanel = true;
            }
        });

        return showPanel;
    }

    async setCommentsInfo(): Promise<boolean> {
        const response = await Api.search("lme/ar/billing-comments", { order_id: this.mainDataSource.activeRow.get("order_id"), customer_id: this.mainDataSource.activeRow.get("customer_id") });
        return response?.data[0]["number_of_comments"] !== 0;
    }

    private setCommentsQuickInfo(image: Image) {
        image.tooltipDynamicContent = async () => {
            const commentLayout = await Layout.getLoadedLayout("lme/ar/BillingCommentsQuickInfo") as BillingCommentsQuickInfo;
            await commentLayout.sourceBillingComments.search({
                order_id: this.mainDataSource.activeRow.get("order_id"),
                customer_id: this.mainDataSource.activeRow.get("customer_id")
            });
            const intRows = commentLayout.sourceBillingComments.data[0].get("int_bill_comments");
            const extRows = commentLayout.sourceBillingComments.data[0].get("ext_bill_comments");

            if (this.areCommentsEmpty(intRows, extRows)) {
                return null;
            }
            this.populateCommentLayout(intRows, commentLayout.layoutIntBillCommentsQuickInfo as IntBillCommentsQuickInfo);
            this.populateCommentLayout(extRows, commentLayout.layoutExtBillCommentsQuickInfo as ExtBillCommentsQuickInfo);
            return commentLayout;
        };
    }

    private areCommentsEmpty(intRows: any[], extRows: any[]): boolean {
        return intRows.length === 0 && extRows.length === 0;
    }

    private populateCommentLayout(rows: any[], layout: IntBillCommentsQuickInfo | ExtBillCommentsQuickInfo) {
        if (rows.length > 0) {
            let table: Table;
            if (layout instanceof IntBillCommentsQuickInfo) {
                table = layout.tableIntBillComments;
            } else {
                table = layout.tableExtBillComments;
            }
            const modelRows = createCommentModelRows(rows);
            table.dataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
        } else {
            layout.visible = false;
        }
    }

    afterCustomerExecution() {
        this.setBridgeCustomerLabel();
    }

    setBridgeCustomerLabel() {
        const bridgeId = this.sourceCustomer?.activeRow?.get("bridge_id");
        if (bridgeId) {
            this.labelBridgeCustomer.visible = true;
        } else {
            this.labelBridgeCustomer.visible = false;
        }
    }
}
